// Taptree tab: a force-directed, pannable, zoomable view of the taproot tree.
//
// A proper force layout (d3-force style): nodes repel (many-body charge), edges
// pull them to a rest length (links, degree-biased), a firm vertical force
// levels each node to its tree depth so rows read straight, a gentle horizontal
// force keeps it centred, and box collision guarantees node bodies never clip.
// Integration is velocity-Verlet with damping and alpha cooling; an alpha
// TARGET (not an alpha spike) keeps the graph warm while you drag, so grabbing
// a node never thrashes. The grabbed node keeps repelling its neighbours (so
// they never melt inward) but skips collision (so you can still drop it onto a
// target to regroup). Drag the canvas or the root to pan; scroll to zoom.
//
// Why depth is the only thing a regroup changes: BIP341 sorts each branch's two
// child hashes, so the address depends ONLY on the multiset of (leaf, depth);
// sibling pairing is cosmetic. Pinning every leaf with @depth reproduces the
// drawn shape exactly (always Kraft-valid, being a real binary tree).
//
// The simulation runs only while warm or dragging, then cools and stops, so an
// idle taptree costs no CPU.

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { ChevronDown, Scale, Workflow } from "lucide-react";
import { useIde } from "../store";
import type { Leaf, TapNode, Taproot } from "../types";
import { depthsOf, modelFromTap, reparentNode, tapFromModel, type ModelTree } from "./taptreeModel";
import { usePricing, spendSats, fmtSats, fmtFiat } from "../pricing";
import { CopyButton, EmptyState, runHint, AsmTokens } from "./shared";

// ── tree walk: keys, depth, parent, leaf set, degree ────────────────────────

interface Laid {
  key: string; // stable identity across restructure AND recompile: keyed by leaf
  // NAME (`L<name>` / `B<name,name>`), not index -- the compiler reassigns leaf
  // indices when it re-plans the tree after a drop, so an index key would change
  // identity and the sim would teleport the node instead of springing it home.
  kind: "leaf" | "branch";
  isRoot: boolean;
  name?: string;
  index?: number;
  depth: number;
  leafSet: number[];
  parentKey: string | null;
  degree: number;
  slot: number; // initial-seed hint only
}

function walkTree(root: TapNode): { nodes: Laid[]; maxSlot: number } {
  const nodes: Laid[] = [];
  let nextSlot = 0;
  function go(node: TapNode, depth: number, isRoot: boolean): { n: Laid; names: string[] } {
    if (node.kind === "leaf") {
      const slot = nextSlot++;
      const n: Laid = { key: `L${node.name}`, kind: "leaf", isRoot: false, name: node.name, index: node.index, depth, leafSet: [node.index], parentKey: null, degree: 1, slot };
      nodes.push(n);
      return { n, names: [node.name] };
    }
    const l = go(node.left, depth + 1, false);
    const r = go(node.right, depth + 1, false);
    const leafSet = [...l.n.leafSet, ...r.n.leafSet].sort((a, b) => a - b);
    // Key the branch by its set of leaf NAMES (stable) rather than indices.
    const names = [...l.names, ...r.names].sort();
    const n: Laid = { key: `B${names.join(",")}`, kind: "branch", isRoot, depth, leafSet, parentKey: null, degree: isRoot ? 2 : 3, slot: (l.n.slot + r.n.slot) / 2 };
    nodes.push(n);
    l.n.parentKey = n.key;
    r.n.parentKey = n.key;
    return { n, names };
  }
  go(root, 0, true);
  return { nodes, maxSlot: Math.max(nextSlot - 1, 0) };
}

/** Keys on the path from a leaf up to the root (for highlighting). */
function pathKeys(nodes: Laid[], leafIndex: number): Set<string> {
  const byKey = new Map(nodes.map((n) => [n.key, n] as const));
  const set = new Set<string>();
  let cur = nodes.find((n) => n.kind === "leaf" && n.index === leafIndex);
  while (cur) {
    set.add(cur.key);
    cur = cur.parentKey ? byKey.get(cur.parentKey) : undefined;
  }
  return set;
}

// ── geometry + force constants ──────────────────────────────────────────────

const LEAF_W = 130;
const LEAF_H = 46; // two lines: spend name on top, cost (sats + fiat) below
const BRANCH_R = 7;
const LEVEL_GAP = 108; // world px between depth levels
const TOP_Y = 40;

const INSPECT_MIN = 240; // narrowest the inspector may be dragged
const TREE_MIN = 220; // tree keeps at least this much when the panel is wide enough

// Critically/slightly-over-damped spring toward each node's FIXED tree-ordered
// rest (tx,ty): v += K*(target-x) - c*v ; x += v. With K=0.05, c=0.40 the
// recurrence has real eigenvalues (0.80, 0.75) => monotonic, ZERO overshoot, no
// bounce; settles ~0.4s. Keep SPRING_C >= ~1.79*sqrt(K) or it begins to bounce.
const K = 0.05;
const SPRING_C = 0.4;

// Displacement-wave field (the fluid coupling). The dragged node's offset from
// its rest propagates one hop per frame along tree edges with per-hop lag and
// attenuation, then bleeds to zero. All three < 1 => a strictly contractive
// low-pass: it flows and lags like honey and can NEVER resonate or oscillate.
const WAVE_EASE = 0.18; // per-hop lag (lower = more trailing honey)
const WAVE_GAIN = 0.92; // fraction of a neighbour's lead inherited per hop (reach)
const WAVE_DECAY = 0.9; // bleed toward zero each frame (relaxation)
const LEAD_MAX = 240; // px clamp so a violent fling cannot launch a node off-canvas
const LEAD_SLEEP = 0.1; // px; a tiny lead with no live neighbour snaps to 0

// Hard sibling-gap clamp: per depth row (slot-sorted) enforce a minimum centre
// gap so two same-depth nodes can NEVER cross. Position-only (injects no velocity).
const MIN_GAP = 84; // floor; per pair we use max(MIN_GAP, hwA+hwB)
const ORDER_ITERS = 2;

// Settle thresholds (loop cools to zero CPU when both hold for every node).
const V_SLEEP = 0.05;
const POS_SLEEP = 0.3;

const SLOT_SPACING = 162; // horizontal gap between leaf slots (> widest node pair)
const LEAF_HW = LEAF_W / 2 + 10; // gap/collision half-extents (with margin)
const LEAF_HH = LEAF_H / 2 + 6;
const BR_HW = 13;
const BR_HH = 13;
const ZOOM_MIN = 0.4;
const ZOOM_MAX = 2.6;

interface PNode {
  key: string;
  x: number;
  y: number;
  vx: number;
  vy: number;
  fx: number | null;
  fy: number | null;
  tx: number; // tree-ordered target X (slot)
  ty: number; // depth-level target Y
  kind: "leaf" | "branch";
  hw: number;
  hh: number;
}

interface DragState {
  mode: "pan" | "node";
  panX0?: number;
  panY0?: number;
  primaryKey?: string;
  dragKeys?: Set<string>;
  index?: number;
  sourceLeafSet?: number[];
  sx0: number;
  sy0: number;
  gx: number; // grab offset: nodeCentre - cursor (world), keeps the grab point under the cursor
  gy: number;
  moved: boolean;
}

// ── component ───────────────────────────────────────────────────────────────

export function TaptreePanel() {
  const result = useIde((s) => s.result);
  const status = useIde((s) => s.status);
  const relayoutActive = useIde((s) => s.relayoutActive);
  const tap: Taproot | undefined = result?.taproot;

  const leavesByName = useMemo(() => {
    const m = new Map<string, Leaf>();
    for (const l of result?.leaves ?? []) m.set(l.name, l);
    return m;
  }, [result?.leaves]);
  const nameOf = useCallback((i: number) => tap?.leaves[i]?.name ?? `#${i}`, [tap?.leaves]);

  // Per-leaf spend cost from result.costs and live pricing. The headline is the
  // full per-INPUT cost (the honest "cost to spend this path": witness + the
  // input's outpoint/sequence at 4 WU/byte). The witness sub-figure is the part
  // that varies between leaves, so it stays available for comparing paths.
  const currency = useIde((s) => s.prefs.currency);
  const pricing = usePricing();
  const costByName = useMemo(() => {
    const m = new Map<
      string,
      { sats: number; satsStr: string; fiatStr: string; vb: number; witnessVb: number; overheadVb: number }
    >();
    const price = pricing.prices[currency] ?? pricing.prices.USD ?? 100000;
    for (const c of result?.costs ?? []) {
      const sats = spendSats(c.maxInputWeight, pricing.feeRate);
      const vb = c.maxInputWeight / 4;
      const witnessVb = c.maxWitnessWeight / 4;
      m.set(c.name, {
        sats,
        satsStr: fmtSats(sats),
        fiatStr: fmtFiat(sats, price, currency),
        vb,
        witnessVb,
        overheadVb: vb - witnessVb, // the fixed 41 vB input base
      });
    }
    return m;
  }, [result?.costs, pricing, currency]);

  const [inspectW, setInspectW] = useState(340); // resizable tree<->inspector split
  const [footOpen, setFootOpen] = useState(true); // global "taproot output" footer
  const [selected, setSelected] = useState<number | null>(null);
  const [hovered, setHovered] = useState<number | null>(null);
  const [draft, setDraft] = useState<ModelTree | null>(null);
  const [draggingKey, setDraggingKey] = useState<string | null>(null);
  const [dropKey, setDropKey] = useState<string | null>(null);
  const [panning, setPanning] = useState(false);

  const currentModel = useMemo(
    () => draft ?? (tap?.tree ? modelFromTap(tap.tree) : null),
    [draft, tap?.tree],
  );
  const renderTap: TapNode | undefined = useMemo(() => {
    if (!tap?.tree) return undefined;
    return draft ? tapFromModel(draft, nameOf) : tap.tree;
  }, [draft, tap?.tree, nameOf]);
  const laid = useMemo(() => (renderTap ? walkTree(renderTap) : null), [renderTap]);

  useEffect(() => {
    setDraft(null);
  }, [result]);

  // ── refs (the sim runs outside React render) ──────────────────────────────
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const viewElRef = useRef<SVGGElement | null>(null);
  const nodeEls = useRef(new Map<string, SVGGElement>());
  const edgeEls = useRef(new Map<string, SVGLineElement>());
  const sim = useRef(new Map<string, PNode>());
  const laidRef = useRef<Laid[]>([]);
  const modelRef = useRef<ModelTree | null>(null);
  const view = useRef({ panX: 0, panY: 0, zoom: 1 });
  const placed = useRef(false);
  const drag = useRef<DragState | null>(null);
  const leadRef = useRef(new Map<string, { x: number; y: number }>()); // wave field
  const childMapRef = useRef(new Map<string, string[]>());
  const rowsRef = useRef<string[][]>([]); // per-depth keys, slot-sorted (order clamp)
  const raf = useRef(0);
  const running = useRef(false);

  const paint = useCallback(() => {
    const v = view.current;
    if (viewElRef.current)
      viewElRef.current.setAttribute("transform", `translate(${v.panX} ${v.panY}) scale(${v.zoom})`);
    for (const n of laidRef.current) {
      const p = sim.current.get(n.key);
      const el = nodeEls.current.get(n.key);
      if (p && el) el.setAttribute("transform", `translate(${p.x} ${p.y})`);
    }
    for (const n of laidRef.current) {
      if (!n.parentKey) continue;
      const line = edgeEls.current.get(n.key);
      const c = sim.current.get(n.key);
      const pa = sim.current.get(n.parentKey);
      if (line && c && pa) {
        line.setAttribute("x1", String(pa.x));
        line.setAttribute("y1", String(pa.y));
        line.setAttribute("x2", String(c.x));
        line.setAttribute("y2", String(c.y));
      }
    }
  }, []);

  const step = useCallback(() => {
    const laidNodes = laidRef.current;
    const lead = leadRef.current;
    const dragKey = drag.current?.mode === "node" ? (drag.current.primaryKey ?? null) : null;

    // 1) SEED the wave from the dragged node's offset off its OWN ordered rest.
    if (dragKey) {
      const dn = sim.current.get(dragKey);
      if (dn) {
        let s = lead.get(dragKey);
        if (!s) {
          s = { x: 0, y: 0 };
          lead.set(dragKey, s);
        }
        s.x = dn.x - dn.tx;
        s.y = dn.y - dn.ty;
      }
    }

    // 2) PROPAGATE the wave ONE hop along tree edges (read a snapshot of last
    //    frame so it is a true travelling wave). A contractive low-pass: never
    //    resonates. The dragged node is the source and is skipped here.
    const prev = new Map<string, { x: number; y: number }>();
    for (const n of laidNodes) {
      const p = lead.get(n.key);
      prev.set(n.key, p ? { x: p.x, y: p.y } : { x: 0, y: 0 });
    }
    const childMap = childMapRef.current;
    for (const n of laidNodes) {
      if (n.key === dragKey) continue;
      let tgx = 0;
      let tgy = 0;
      let best = -1;
      const consider = (k: string | null | undefined) => {
        if (!k) return;
        const p = prev.get(k);
        if (!p) return;
        const m = p.x * p.x + p.y * p.y;
        if (m > best) {
          best = m;
          tgx = p.x * WAVE_GAIN;
          tgy = p.y * WAVE_GAIN;
        }
      };
      consider(n.parentKey);
      for (const c of childMap.get(n.key) ?? []) consider(c);
      let cur = lead.get(n.key);
      if (!cur) {
        cur = { x: 0, y: 0 };
        lead.set(n.key, cur);
      }
      cur.x = (cur.x + (tgx - cur.x) * WAVE_EASE) * WAVE_DECAY;
      cur.y = (cur.y + (tgy - cur.y) * WAVE_EASE) * WAVE_DECAY;
      const mag = Math.hypot(cur.x, cur.y);
      if (mag > LEAD_MAX) {
        const sc = LEAD_MAX / mag;
        cur.x *= sc;
        cur.y *= sc;
      }
      if (mag < LEAD_SLEEP && best <= 0) {
        cur.x = 0;
        cur.y = 0;
      }
    }
    if (!dragKey) for (const [, l] of lead) ((l.x *= WAVE_DECAY), (l.y *= WAVE_DECAY)); // bleed after release

    // 3) INTEGRATE: critically/over-damped spring toward (rest + lead). Pinned
    //    (dragged) node is position-authoritative (no spring, no drift).
    let active = !!dragKey;
    for (const n of laidNodes) {
      const p = sim.current.get(n.key);
      if (!p) continue;
      if (p.fx !== null && p.fy !== null) {
        p.x = p.fx;
        p.y = p.fy;
        p.vx = 0;
        p.vy = 0;
        continue;
      }
      const ld = lead.get(n.key);
      const tx = p.tx + (ld ? ld.x : 0);
      const ty = p.ty + (ld ? ld.y : 0);
      p.vx += K * (tx - p.x) - SPRING_C * p.vx;
      p.vy += K * (ty - p.y) - SPRING_C * p.vy;
      p.x += p.vx;
      p.y += p.vy;
      if (
        Math.hypot(p.vx, p.vy) > V_SLEEP ||
        Math.hypot(tx - p.x, ty - p.y) > POS_SLEEP ||
        (ld && (ld.x || ld.y))
      )
        active = true;
    }

    // 4) HARD sibling-gap clamp per depth row (slot-sorted). Makes crossing
    //    geometrically impossible. Position-only (no velocity => cannot pump a
    //    bounce). The dragged node is excluded (it moves freely to a drop target;
    //    the wave is what flows its neighbours aside).
    const rows = rowsRef.current;
    for (let it = 0; it < ORDER_ITERS; it++) {
      for (const row of rows) {
        for (let i = 0; i + 1 < row.length; i++) {
          const a = sim.current.get(row[i]);
          const b = sim.current.get(row[i + 1]);
          if (!a || !b || a.key === dragKey || b.key === dragKey) continue;
          const need = Math.max(MIN_GAP, a.hw + b.hw);
          const gap = b.x - a.x;
          if (gap < need) {
            const push = (need - gap) * 0.5;
            a.x -= push;
            b.x += push;
            a.vx *= 0.5;
            b.vx *= 0.5;
          }
        }
      }
    }

    paint();
    return active;
  }, [paint]);

  const loop = useCallback(() => {
    if (step()) raf.current = requestAnimationFrame(loop);
    else running.current = false;
  }, [step]);

  const ensureRunning = useCallback(() => {
    if (!running.current) {
      running.current = true;
      raf.current = requestAnimationFrame(loop);
    }
  }, [loop]);

  // Frame the whole tree in the viewport (first render + "Reset view").
  const fitView = useCallback(() => {
    const r = wrapRef.current?.getBoundingClientRect();
    const W = r && r.width > 10 ? r.width : 600;
    const H = r && r.height > 10 ? r.height : 360;
    let maxDepth = 0;
    let n = 0;
    for (const ln of laidRef.current) {
      maxDepth = Math.max(maxDepth, ln.depth);
      if (ln.kind === "leaf") n++;
    }
    const treeW = Math.max(n, 1) * (LEAF_W + 26) + 40;
    const treeH = maxDepth * LEVEL_GAP + LEAF_H + 110;
    const zoom = Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, Math.min(W / treeW, H / treeH, 1)));
    const cyWorld = TOP_Y + (maxDepth * LEVEL_GAP) / 2;
    view.current = { panX: W / 2, panY: H / 2 - cyWorld * zoom, zoom };
    paint();
  }, [paint]);

  // Sync the sim to the current tree: keep existing nodes (they spring to the new
  // ordered rest), seed new ones at their parent, drop departed ones. Also rebuild
  // the child map + slot-sorted depth rows, and clear the wave field (stale keys
  // would inject phantom displacement). No reheat: the springs are always active
  // and self-settle; the coupling lives in the (self-extinguishing) wave.
  useLayoutEffect(() => {
    if (!laid) return;
    laidRef.current = laid.nodes;
    modelRef.current = currentModel;
    const ms = laid.maxSlot;
    const seen = new Set<string>();
    for (const ln of laid.nodes) {
      seen.add(ln.key);
      const tx = (ln.slot - ms / 2) * SLOT_SPACING;
      const ty = TOP_Y + ln.depth * LEVEL_GAP;
      const hw = ln.kind === "leaf" ? LEAF_HW : BR_HW;
      const hh = ln.kind === "leaf" ? LEAF_HH : BR_HH;
      const p = sim.current.get(ln.key);
      if (p) {
        p.tx = tx;
        p.ty = ty;
        p.kind = ln.kind;
        p.hw = hw;
        p.hh = hh;
      } else {
        const parent = ln.parentKey ? sim.current.get(ln.parentKey) : null;
        const sx = parent ? parent.x : tx;
        const sy = parent ? parent.y : ty;
        sim.current.set(ln.key, { key: ln.key, x: sx, y: sy, vx: 0, vy: 0, fx: null, fy: null, tx, ty, kind: ln.kind, hw, hh });
      }
    }
    for (const k of [...sim.current.keys()]) if (!seen.has(k)) sim.current.delete(k);
    leadRef.current.clear();
    // child map (for wave propagation) + slot-sorted rows (for the order clamp)
    const childMap = new Map<string, string[]>();
    const byDepth = new Map<number, Laid[]>();
    for (const ln of laid.nodes) {
      if (ln.parentKey) (childMap.get(ln.parentKey) ?? childMap.set(ln.parentKey, []).get(ln.parentKey)!).push(ln.key);
      (byDepth.get(ln.depth) ?? byDepth.set(ln.depth, []).get(ln.depth)!).push(ln);
    }
    childMapRef.current = childMap;
    rowsRef.current = [...byDepth.values()].map((arr) => arr.sort((a, b) => a.slot - b.slot).map((n) => n.key));
    if (!placed.current) {
      fitView();
      placed.current = true;
    }
    paint();
    ensureRunning();
  }, [laid, currentModel, paint, ensureRunning, fitView]);

  // Wheel-zoom toward the cursor (non-passive so we can prevent page scroll).
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const r = el.getBoundingClientRect();
      const sx = e.clientX - r.left;
      const sy = e.clientY - r.top;
      const v = view.current;
      const z = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, v.zoom * Math.exp(-e.deltaY * 0.0015)));
      const k = z / v.zoom;
      v.panX = sx - (sx - v.panX) * k;
      v.panY = sy - (sy - v.panY) * k;
      v.zoom = z;
      paint();
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [paint]);

  useEffect(
    () => () => {
      cancelAnimationFrame(raf.current);
      running.current = false;
    },
    [],
  );

  // ── pointer helpers ────────────────────────────────────────────────────────
  const toScreen = useCallback((e: { clientX: number; clientY: number }) => {
    const el = wrapRef.current;
    if (!el) return { x: 0, y: 0 };
    const r = el.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  }, []);
  const toWorld = useCallback((e: { clientX: number; clientY: number }) => {
    const g = viewElRef.current;
    const ctm = g?.getScreenCTM();
    if (!g || !ctm) return { x: 0, y: 0 };
    const p = new DOMPoint(e.clientX, e.clientY).matrixTransform(ctm.inverse());
    return { x: p.x, y: p.y };
  }, []);

  const beginPan = useCallback(
    (e: React.PointerEvent) => {
      if (e.button !== 0) return;
      try {
        e.currentTarget.setPointerCapture(e.pointerId);
      } catch {
        /* best effort */
      }
      const s = toScreen(e);
      drag.current = { mode: "pan", panX0: view.current.panX, panY0: view.current.panY, sx0: s.x, sy0: s.y, gx: 0, gy: 0, moved: false };
      setPanning(true);
    },
    [toScreen],
  );

  const beginNode = useCallback(
    (e: React.PointerEvent, n: Laid) => {
      if (e.button !== 0) return;
      e.stopPropagation();
      if (n.isRoot) {
        beginPan(e); // the root is the handle for panning the whole tree
        return;
      }
      // Branches are NOT draggable: a branch has no independent position (its rest
      // is the average of its children's slots), so dragging one is exactly the
      // "reorder a branch" motion that tangled. Only leaves drive regrouping.
      if (n.kind !== "leaf") return;
      try {
        e.currentTarget.setPointerCapture(e.pointerId);
      } catch {
        /* best effort */
      }
      const w = toWorld(e);
      const p = sim.current.get(n.key);
      // Pin in place immediately so it does not drift while held without moving;
      // remember the grab offset so the grabbed point stays under the cursor.
      const gx = p ? p.x - w.x : 0;
      const gy = p ? p.y - w.y : 0;
      if (p) {
        p.fx = p.x;
        p.fy = p.y;
        p.vx = 0;
        p.vy = 0;
      }
      drag.current = { mode: "node", primaryKey: n.key, dragKeys: new Set([n.key]), index: n.index, sourceLeafSet: n.leafSet, sx0: w.x, sy0: w.y, gx, gy, moved: false };
      ensureRunning();
    },
    [beginPan, toWorld, ensureRunning],
  );

  const onMove = useCallback(
    (e: React.PointerEvent) => {
      const d = drag.current;
      if (!d) return;
      if (d.mode === "pan") {
        const s = toScreen(e);
        view.current.panX = (d.panX0 as number) + (s.x - d.sx0);
        view.current.panY = (d.panY0 as number) + (s.y - d.sy0);
        d.moved = true;
        paint();
        return;
      }
      const w = toWorld(e);
      if (!d.moved && Math.hypot(w.x - d.sx0, w.y - d.sy0) > 4) {
        d.moved = true;
        setDraggingKey(d.primaryKey as string);
      }
      const primary = sim.current.get(d.primaryKey as string);
      if (primary) {
        primary.fx = w.x + d.gx; // keep the grabbed point under the cursor
        primary.fy = w.y + d.gy;
      }
      if (d.moved) {
        let best: { key: string; dist: number } | null = null;
        for (const n of laidRef.current) {
          if (d.dragKeys?.has(n.key)) continue; // not onto itself / its own subtree
          const q = sim.current.get(n.key);
          if (!q) continue;
          const dist = Math.hypot(q.x - w.x, q.y - w.y);
          const reach = n.kind === "leaf" ? 72 : 34;
          if (dist <= reach && dist < (best?.dist ?? Infinity)) best = { key: n.key, dist };
        }
        setDropKey((prev) => (prev === (best?.key ?? null) ? prev : best?.key ?? null));
      }
      ensureRunning();
    },
    [toScreen, toWorld, paint, ensureRunning],
  );

  const commit = useCallback(
    (tree: ModelTree) => {
      const depths = depthsOf(tree);
      const byName: Record<string, number | null> = {};
      for (const [i, dp] of depths) byName[nameOf(i)] = dp;
      void relayoutActive(byName);
    },
    [nameOf, relayoutActive],
  );

  const onUp = useCallback(
    (e: React.PointerEvent) => {
      try {
        e.currentTarget.releasePointerCapture(e.pointerId);
      } catch {
        /* already released */
      }
      const d = drag.current;
      const target = dropKey;
      // release the pin
      if (d?.mode === "node" && d.primaryKey) {
        const p = sim.current.get(d.primaryKey);
        if (p) {
          p.fx = null;
          p.fy = null;
        }
      }
      drag.current = null;
      setDraggingKey(null);
      setDropKey(null);
      setPanning(false);
      if (!d) return;
      if (d.mode === "pan") return;
      if (!d.moved) {
        if (d.index != null) setSelected((s) => (s === d.index ? null : (d.index ?? null)));
        return;
      }
      if (target) {
        const targetNode = laidRef.current.find((n) => n.key === target);
        const model = modelRef.current;
        if (targetNode && model) {
          const next = reparentNode(model, d.sourceLeafSet as number[], targetNode.leafSet);
          if (next) {
            setDraft(next);
            commit(next);
          }
        }
      }
      // The released node keeps its velocity (follow-through) and the over-damped
      // spring + decaying wave glide everyone home with zero overshoot.
      ensureRunning();
    },
    [dropKey, commit, ensureRunning],
  );

  const autoBalance = useCallback(() => {
    if (!tap) return;
    const byName: Record<string, number | null> = {};
    for (const l of tap.leaves) byName[l.name] = null;
    setDraft(null);
    void relayoutActive(byName);
  }, [tap, relayoutActive]);

  const resetView = useCallback(() => fitView(), [fitView]);

  // Resizable tree <-> inspector split.
  const pnTapRef = useRef<HTMLDivElement | null>(null);
  const resizing = useRef(false);
  const onResizeDown = useCallback((e: React.PointerEvent) => {
    if (e.button !== 0) return;
    resizing.current = true;
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {
      /* best effort */
    }
  }, []);
  const onResizeMove = useCallback((e: React.PointerEvent) => {
    if (!resizing.current) return;
    const r = pnTapRef.current?.getBoundingClientRect();
    if (!r) return;
    // Keep room for the tree when the panel is wide enough; degrade gracefully
    // (never force the inspector wider than the panel) when it is cramped.
    const max = Math.max(INSPECT_MIN, r.width - TREE_MIN);
    setInspectW(Math.min(max, Math.max(INSPECT_MIN, r.right - e.clientX)));
  }, []);
  const onResizeUp = useCallback((e: React.PointerEvent) => {
    resizing.current = false;
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      /* already released */
    }
  }, []);

  if (!tap) {
    return (
      <div className="pn-scroll">
        <EmptyState
          icon={<Workflow size={22} strokeWidth={1.5} />}
          title={status === "compiling" ? "Compiling…" : "No taptree yet"}
          hint={runHint("the taproot tree, leaf scripts and control blocks")}
        />
      </div>
    );
  }

  const focus = hovered ?? selected;
  const lit = laid && focus != null ? pathKeys(laid.nodes, focus) : new Set<string>();
  const selectedLeaf = selected != null ? tap.leaves[selected] : null;
  const selectedScript = selectedLeaf ? leavesByName.get(selectedLeaf.name) : null;

  return (
    <div className="pn-tap" ref={pnTapRef}>
      <div className="pn-tap-main">
        {laid && (
          <div className="pn-tap-bar">
            <span className="pn-tap-hint">
              Drag a spend onto another to regroup; drag the canvas to pan, scroll to zoom.
            </span>
            <button type="button" className="pn-tap-reset pn-tap-reset--ghost" onClick={resetView} title="Recenter the view">
              Reset view
            </button>
            <button
              type="button"
              className="pn-tap-reset"
              onClick={autoBalance}
              title="Remove all @depth pins and let the planner balance the tree by @weight"
            >
              <Scale size={13} strokeWidth={2} />
              <span>Auto-balance</span>
            </button>
          </div>
        )}
        <div ref={wrapRef} className={`pn-tap-canvas${panning ? " is-panning" : ""}`}>
          {laid ? (
            <svg className="pn-tap-svg" width="100%" height="100%" role="img" aria-label="Taproot script tree">
              <rect
                className="pn-tap-bg"
                x={0}
                y={0}
                width="100%"
                height="100%"
                onPointerDown={beginPan}
                onPointerMove={onMove}
                onPointerUp={onUp}
              />
              <g ref={viewElRef}>
                {laid.nodes
                  .filter((n) => n.parentKey)
                  .map((n) => (
                    <line
                      key={`e-${n.key}`}
                      ref={(el) => {
                        if (el) edgeEls.current.set(n.key, el);
                      }}
                      className={`pn-tap-edge${lit.has(n.key) && lit.has(n.parentKey!) ? " is-lit" : ""}`}
                    />
                  ))}

                {laid.nodes
                  .filter((n) => n.kind === "branch")
                  .map((n) => (
                    <g
                      key={n.key}
                      ref={(el) => {
                        if (el) nodeEls.current.set(n.key, el);
                      }}
                      className={`pn-tap-branch${lit.has(n.key) ? " is-lit" : ""}${
                        dropKey === n.key ? " is-drop" : ""
                      }${draggingKey === n.key ? " is-dragging" : ""}${n.isRoot ? " is-root" : ""}`}
                      onPointerDown={(e) => beginNode(e, n)}
                      onPointerMove={onMove}
                      onPointerUp={onUp}
                    >
                      <circle className="pn-tap-branch-hit" cx={0} cy={0} r={16} />
                      <circle className="pn-tap-branch-dot" cx={0} cy={0} r={n.isRoot ? BRANCH_R + 1.5 : BRANCH_R} />
                      {n.isRoot && (
                        <text className="pn-tap-rootlabel" x={0} y={-15}>
                          merkle root
                        </text>
                      )}
                    </g>
                  ))}

                {laid.nodes
                  .filter((n) => n.kind === "leaf")
                  .map((n) => {
                    const isSel = selected === n.index;
                    const cost = n.name ? costByName.get(n.name) : undefined;
                    return (
                      <g
                        key={n.key}
                        ref={(el) => {
                          if (el) nodeEls.current.set(n.key, el);
                        }}
                        className={`pn-tap-leaf${isSel ? " is-selected" : ""}${lit.has(n.key) ? " is-lit" : ""}${
                          draggingKey === n.key ? " is-dragging" : ""
                        }${dropKey === n.key ? " is-drop" : ""}`}
                        onPointerDown={(e) => beginNode(e, n)}
                        onPointerMove={onMove}
                        onPointerUp={onUp}
                        onPointerEnter={() => !drag.current && setHovered(n.index ?? null)}
                        onPointerLeave={() => !drag.current && setHovered(null)}
                      >
                        <rect x={-LEAF_W / 2} y={-LEAF_H / 2} width={LEAF_W} height={LEAF_H} rx={8} />
                        <text className="pn-tap-leaf-name" x={0} y={cost ? -7 : 1}>
                          {n.name}
                        </text>
                        {cost && (
                          <text className="pn-tap-leaf-cost" x={0} y={12}>
                            {cost.satsStr} sats · {cost.fiatStr}
                          </text>
                        )}
                        {n.depth > 0 && (
                          <text className="pn-tap-leaf-depth" x={LEAF_W / 2 - 9} y={-LEAF_H / 2 + 10}>
                            d{n.depth}
                          </text>
                        )}
                      </g>
                    );
                  })}
              </g>
            </svg>
          ) : (
            <div className="pn-tap-keyonly">
              Key-path only output: the internal key spends directly, with no script tree.
            </div>
          )}
        </div>
      </div>

      <div
        className="pn-tap-resizer"
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize the inspector"
        onPointerDown={onResizeDown}
        onPointerMove={onResizeMove}
        onPointerUp={onResizeUp}
      />

      <aside className="pn-tap-inspect" style={{ flex: "0 0 auto", width: inspectW }}>
        <div className="qi">
          <div className="qi-scroll">
            {selectedLeaf ? (
              <>
                <header className="qi-head">
                  <span className="qi-name">{selectedLeaf.name}</span>
                  <span className="qi-depth">depth {selectedLeaf.depth}</span>
                </header>

                {(() => {
                  const c = costByName.get(selectedLeaf.name);
                  if (!c) return null;
                  const w = c.vb > 0 ? Math.max(0, Math.min(1, c.witnessVb / c.vb)) : 0;
                  return (
                    <div className="qi-cost">
                      <div className="qi-cost-top">
                        <span>
                          <span className="qi-sats">{c.satsStr}</span>
                          <span className="qi-sats-u">sats</span>
                        </span>
                        <span className="qi-fiat">
                          {c.fiatStr}
                          {!pricing.live && " est."}
                        </span>
                      </div>
                      <div className="qi-bar">
                        <span className="qi-bar-w" style={{ width: `${w * 100}%` }} />
                      </div>
                      <div className="qi-cost-sub">
                        {c.witnessVb.toFixed(2)} witness · {c.overheadVb.toFixed(2)} input ·{" "}
                        {pricing.feeRate} sat/vB
                      </div>
                    </div>
                  );
                })()}

                {selectedScript && (
                  <>
                    <Section label="Witness order">
                      {selectedScript.witnessOrder.length ? (
                        <div className="qi-tags">
                          {selectedScript.witnessOrder.map((n, i) => (
                            <span className="qi-tag" key={`${n}-${i}`}>
                              <span className="qi-tag-i">{i}</span>
                              {n}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="qi-none">none, spent by script alone</span>
                      )}
                    </Section>

                    <Section
                      label="Disassembly"
                      meta={
                        <>
                          {selectedScript.bytes} B
                          <span className="qi-copy qi-copy--inline">
                            <CopyButton value={selectedScript.asm} label="" title="Copy disassembly" />
                          </span>
                        </>
                      }
                    >
                      <pre className="qi-code">
                        <AsmTokens asm={selectedScript.asm} />
                      </pre>
                    </Section>

                    <Section
                      label="Hex"
                      meta={
                        <span className="qi-copy qi-copy--inline">
                          <CopyButton value={selectedScript.hex} label="" title="Copy hex" />
                        </span>
                      }
                    >
                      <pre className="qi-code qi-code--dim">{selectedScript.hex}</pre>
                    </Section>
                  </>
                )}

                <Section label="Commitment">
                  <HashRow label="tapleaf" value={selectedLeaf.tapleafHash} />
                  <HashRow label="control" value={selectedLeaf.controlBlock} />
                </Section>

                <Section label="Merkle path">
                  {selectedLeaf.merklePath.length ? (
                    selectedLeaf.merklePath.map((h, i) => (
                      <HashRow key={i} label={`sibling ${i}`} value={h} />
                    ))
                  ) : (
                    <span className="qi-none">sole leaf, no siblings</span>
                  )}
                </Section>
              </>
            ) : tap.merkleRoot ? (
              <div className="qi-empty">Select a leaf to inspect it.</div>
            ) : (
              <header className="qi-head">
                <span className="qi-name qi-name--kp">key-path only</span>
              </header>
            )}
          </div>

          <div className={`qi-foot${footOpen ? "" : " qi-collapsed"}`}>
            <button
              type="button"
              className="qi-foot-lab"
              onClick={() => setFootOpen((o) => !o)}
              aria-expanded={footOpen}
            >
              Taproot output
              <ChevronDown size={14} strokeWidth={2} className="qi-chev" aria-hidden="true" />
            </button>
            {footOpen && (
              <div className="qi-foot-body">
                <HashRow label="output" value={tap.outputKey} />
                <HashRow label="internal" value={tap.internalKey} />
                {tap.merkleRoot && <HashRow label="merkle root" value={tap.merkleRoot} />}
                <HashRow label="taptweak" value={tap.tweak} />
                <div className="qi-row">
                  <span className="qi-row-label">parity</span>
                  <span className="qi-row-val">
                    <span className="qi-mono">{tap.parity}</span>
                    <span className="qi-copy qi-copy--spacer" aria-hidden="true" />
                  </span>
                </div>
                {tap.numsR && <HashRow label="nums r" value={tap.numsR} />}
                {!tap.merkleRoot && (
                  <div className="qi-none qi-foot-note">key-path only, no script tree</div>
                )}
              </div>
            )}
          </div>
        </div>
      </aside>
    </div>
  );
}

// ── inspector primitives (the "quiet" flat panel) ────────────────────────────

/** Middle-truncate a long hex value: head…tail. */
function trunc(s: string, h = 8, t = 8): string {
  return s.length > h + t + 1 ? `${s.slice(0, h)}…${s.slice(-t)}` : s;
}

/** A labelled group, set off from the previous one by a hairline. */
function Section({ label, meta, children }: { label: string; meta?: ReactNode; children: ReactNode }) {
  return (
    <section className="qi-sec">
      <div className="qi-sec-head">
        <span className="qi-sec-label">{label}</span>
        {meta != null && <span className="qi-sec-meta">{meta}</span>}
      </div>
      {children}
    </section>
  );
}

/** One hash line: UI-font label left, middle-truncated mono value right, copy on
 *  hover. The single treatment for every hash (commitment, siblings, output). */
function HashRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="qi-row">
      <span className="qi-row-label">{label}</span>
      <span className="qi-row-val">
        <span className="qi-mono" title={value}>
          {trunc(value)}
        </span>
        <span className="qi-copy">
          <CopyButton value={value} label="" title={`Copy ${label}`} />
        </span>
      </span>
    </div>
  );
}
